import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Camera, Video, Heart, Check, Star, Users, Award, Search, ChevronRight, X, Mail, Cookie } from 'lucide-react';
import axios from 'axios';
import '@/index.css';
import { BlogListPage, BlogPostPage } from './components/BlogPages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUIZ_QUESTIONS = [
  {
    id: 'service_type',
    question: 'What are you looking for?',
    options: [
      { value: 'photography', label: 'Photography only', icon: Camera },
      { value: 'videography', label: 'Videography only', icon: Video },
      { value: 'both', label: 'Both photography and videography', icon: Heart }
    ]
  },
  {
    id: 'style_preference',
    question: 'Which style resonates with you most?',
    options: [
      { value: 'documentary', label: 'Documentary - authentic, candid moments' },
      { value: 'cinematic', label: 'Cinematic - artistic, film-like storytelling' },
      { value: 'traditional', label: 'Traditional - classic, timeless elegance' },
      { value: 'modern', label: 'Modern - creative, contemporary approach' }
    ]
  },
  {
    id: 'moments_priority',
    question: 'What moments matter most to you?',
    options: [
      { value: 'couple_shots', label: 'Beautiful portraits of us as a couple' },
      { value: 'candid_moments', label: 'Authentic, unposed moments with guests' },
      { value: 'emotional_story', label: 'The emotional story of our day' },
      { value: 'details', label: 'Every detail and formal moments' }
    ]
  },
  {
    id: 'emotional_tone',
    question: 'How do you want to remember your day?',
    options: [
      { value: 'romantic', label: 'Romantic and intimate' },
      { value: 'joyful', label: 'Joyful and celebratory' },
      { value: 'elegant', label: 'Elegant and sophisticated' },
      { value: 'authentic', label: 'Real and authentic' }
    ]
  },
  {
    id: 'speeches_importance',
    question: 'How important are speeches and vows?',
    options: [
      { value: 'very_important', label: 'Essential - they tell our story' },
      { value: 'important', label: 'Important - want key moments captured' },
      { value: 'nice_to_have', label: 'Nice to have, but not the focus' },
      { value: 'not_priority', label: 'Not a priority for us' }
    ]
  }
];

function LandingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [businessForm, setBusinessForm] = useState({
    name: '',
    business_name: '',
    website: '',
    supplier_type: '',
    instagram_handle: ''
  });
  const [businessSubmitting, setBusinessSubmitting] = useState(false);
  const [businessSubmitted, setBusinessSubmitted] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        // Not authenticated
      }
    };
    checkAuth();
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setBusinessSubmitting(true);
    try {
      await axios.post(`${API}/submit-business`, businessForm);
      setBusinessSubmitted(true);
      setTimeout(() => {
        setShowBusinessModal(false);
        setBusinessSubmitted(false);
        setBusinessForm({
          name: '',
          business_name: '',
          website: '',
          supplier_type: '',
          instagram_handle: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting business:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setBusinessSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div className="texture-overlay" />
      
      {/* Header */}
      <header className="relative z-20 px-6 md:px-12 py-6 bg-white/95 backdrop-blur-md border-b border-charcoal/5 sticky top-0">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="font-heading text-2xl font-bold tracking-tight text-charcoal leading-none">
                Essex Photo & Video
              </div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold font-semibold mt-1">
                Finder
              </div>
            </div>
          </div>
          {user ? (
            <Link
              to="/dashboard"
              data-testid="dashboard-link"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-charcoal text-white text-sm font-medium hover:bg-charcoal/90 transition-all duration-300"
            >
              Dashboard
            </Link>
          ) : (
            <button
              onClick={() => navigate('/quiz')}
              data-testid="header-quiz-btn"
              className="flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-sm font-bold uppercase tracking-wide hover:shadow-xl transition-all duration-300"
            >
              Take Quiz
            </button>
          )}
        </div>
      </header>

      {/* Hero Section with Full-Width Image */}
      <section className="relative z-10 h-[85vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=90"
            alt="Wedding couple"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        <div className="relative z-10 px-6 md:px-12 max-w-[1400px] mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-xs uppercase tracking-wider text-white/90 font-semibold">AI-Powered Matching</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold tracking-tight leading-[1.1] mb-6 text-white">
              Find Your Perfect
              <span className="block gradient-text mt-2">Wedding Photographer</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl leading-relaxed">
              Answer a few questions and let our intelligent matching system connect you with Essex's finest photographers and videographers who match your unique style.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/quiz')}
                data-testid="start-quiz-btn"
                className="group flex items-center gap-3 px-10 py-5 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-base font-bold uppercase tracking-wide hover:shadow-2xl hover:shadow-gold/30 transition-all duration-500 hover:scale-105"
              >
                Start Your Match
                <ArrowRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1" />
              </button>
            </div>
            
            <p className="mt-6 text-white/70 text-sm font-medium">
              ✓ 100% Free · ✓ No Sign Up Required · ✓ Takes 30 Seconds
            </p>

            <div className="flex items-center gap-8 mt-8 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-gold fill-gold" />
                <span className="text-white/90 text-sm font-medium">Only 5* Rated Professionals</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" />
                <span className="text-white/90 text-sm font-medium">500+ Happy Couples</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-gold" />
                <span className="text-white/90 text-sm font-medium">Award-Winning Teams</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Suppliers Preview */}
      <section className="relative z-10 px-6 md:px-12 py-24 bg-gradient-to-b from-white to-pearl">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-5xl md:text-6xl font-heading font-bold tracking-tight mb-4 text-charcoal">
                Featured Suppliers
              </h2>
              <p className="text-lg text-charcoal/60 max-w-2xl mx-auto">
                Discover Essex's most talented wedding photographers and videographers
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Wedding Video Essex',
                type: 'Cinematic Videography',
                bio: 'Award-winning team specializing in cinematic wedding films that capture authentic emotions and tell your unique love story.',
                image: 'https://customer-assets.emergentagent.com/job_cine-search/artifacts/ti0hsg4k_WVE%20MAIN%20PHOTO.jpg',
                rating: 5.0,
                imagePosition: 'object-top'
              },
              {
                name: 'Chic Wedding Photography',
                type: 'Luxury Photography',
                bio: 'Elegant and sophisticated wedding photography with a focus on timeless couple portraits and stunning details.',
                image: 'https://customer-assets.emergentagent.com/job_cine-search/artifacts/9u2bjc1t_chic.avif',
                rating: 5.0,
                imagePosition: 'object-top'
              },
              {
                name: 'Click Play Weddings',
                type: 'Creative Videography',
                bio: 'Modern and creative wedding videography with a stylish approach, capturing your special day with artistic flair.',
                image: 'https://customer-assets.emergentagent.com/job_477ea6d8-7123-4c8c-8496-4727ee5a75ee/artifacts/llhsdafz_CHIC%20NEWS%20ANNOUNCE.jpg',
                rating: 5.0,
                imagePosition: 'object-bottom'
              }
            ].map((supplier, index) => (
              <motion.div
                key={supplier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={supplier.image}
                    alt={supplier.name}
                    className={`w-full h-full object-cover ${supplier.imagePosition || 'object-top'} transition-transform duration-700 group-hover:scale-105`}
                  />
                  <div className="image-overlay" />
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur-sm flex items-center gap-1">
                    <Star className="w-4 h-4 text-gold fill-gold" />
                    <span className="text-sm font-bold text-charcoal">{supplier.rating}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-heading font-semibold mb-2 text-charcoal">
                    {supplier.name}
                  </h3>
                  <p className="text-charcoal/60 text-sm mb-4">{supplier.type}</p>
                  <p className="text-charcoal/70 text-sm leading-relaxed mb-6">
                    {supplier.bio}
                  </p>
                  <button
                    onClick={() => navigate('/quiz')}
                    className="w-full py-3 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-semibold text-sm uppercase tracking-wide hover:shadow-lg transition-all duration-300"
                  >
                    Take Quiz to See If You're a Perfect Match
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 px-6 md:px-12 py-24 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-heading font-bold tracking-tight mb-4 text-charcoal">
              How It Works
            </h2>
            <p className="text-lg text-charcoal/60 max-w-2xl mx-auto">
              Three simple steps to find your perfect match
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { 
                number: '01', 
                icon: Heart,
                title: 'Take the Quiz', 
                description: 'Answer thoughtful questions about your wedding vision, style preferences, and what matters most to you'
              },
              { 
                number: '02', 
                icon: Sparkles,
                title: 'AI Matching', 
                description: 'Our intelligent system analyzes your answers using advanced AI to find suppliers that perfectly match your style'
              },
              { 
                number: '03', 
                icon: Camera,
                title: 'Discover Matches', 
                description: 'Get personalized recommendations with match scores, portfolios, and direct contact links'
              }
            ].map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gold to-gold-dim flex items-center justify-center mb-6 shadow-lg">
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-8xl font-heading font-bold text-charcoal/5 absolute top-0 right-0 -z-10">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-3xl font-heading font-semibold mb-4 text-charcoal">{step.title}</h3>
                <p className="text-base text-charcoal/60 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => navigate('/quiz')}
              className="inline-flex items-center gap-2 px-10 py-5 rounded-full bg-charcoal text-white text-base font-bold uppercase tracking-wide hover:bg-charcoal/90 transition-all duration-300 hover:scale-105"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 md:px-12 py-32 bg-gradient-to-br from-charcoal via-charcoal to-[#2A2A2A]">
        <div className="max-w-[1000px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-6xl font-heading font-bold tracking-tight mb-6 text-white">
              Ready to Find Your Match?
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Join hundreds of happy couples who found their perfect photographer through our platform
            </p>
            <button
              onClick={() => navigate('/quiz')}
              data-testid="cta-quiz-btn"
              className="inline-flex items-center gap-3 px-12 py-6 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-lg font-bold uppercase tracking-wide hover:shadow-2xl hover:shadow-gold/30 transition-all duration-500 hover:scale-105"
            >
              Start Your Match
              <ArrowRight className="w-6 h-6" />
            </button>
            <p className="mt-6 text-white/60 text-sm font-medium">
              ✓ 100% Free · ✓ No Sign Up Required · ✓ Takes 30 Seconds
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-12 bg-charcoal border-t border-white/10">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <div className="font-heading text-xl font-bold text-white mb-2">Essex Photo & Video Finder</div>
              <p className="text-white/50 text-sm">AI-Powered Wedding Supplier Matching</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <Link to="/blog" className="text-white/70 hover:text-gold transition-colors text-sm font-medium">
                Wedding Guide & FAQs
              </Link>
              <button
                onClick={() => setShowBusinessModal(true)}
                data-testid="submit-business-btn"
                className="text-white/70 hover:text-gold transition-colors text-sm font-medium"
              >
                Submit Your Business
              </button>
              <button
                onClick={() => navigate('/quiz')}
                className="px-6 py-2.5 rounded-full bg-gold/20 text-gold text-sm font-bold hover:bg-gold/30 transition-colors"
              >
                Take Quiz
              </button>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-xs">
              © {new Date().getFullYear()} Essex Photo & Video Finder. Helping couples find their perfect wedding photographer and videographer.
            </p>
            <p className="text-white/50 text-xs mt-3">
              Part of the{' '}
              <a 
                href="https://www.wvegroup.co.uk/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold hover:text-gold-dim transition-colors underline"
              >
                WVE Group
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Business Submission Modal */}
      <AnimatePresence>
        {showBusinessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowBusinessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl p-8 md:p-12 max-w-xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowBusinessModal(false)}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-charcoal/5 hover:bg-charcoal/10 flex items-center justify-center transition-colors duration-300"
              >
                <X className="w-5 h-5 text-charcoal" />
              </button>

              {businessSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-charcoal mb-3">
                    Submission Received!
                  </h3>
                  <p className="text-charcoal/60">
                    Thank you for your interest. We'll review your submission and be in touch soon.
                  </p>
                </motion.div>
              ) : (
                <>
                  <div className="mb-8">
                    <h2 className="text-3xl font-heading font-bold text-charcoal mb-3">
                      Submit Your Business
                    </h2>
                    <p className="text-charcoal/60">
                      Interested in being featured on Essex Photo & Video Finder? Tell us about your business.
                    </p>
                  </div>

                  <form onSubmit={handleBusinessSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-charcoal mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={businessForm.name}
                        onChange={(e) => setBusinessForm({...businessForm, name: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-charcoal/10 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-charcoal mb-2">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={businessForm.business_name}
                        onChange={(e) => setBusinessForm({...businessForm, business_name: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-charcoal/10 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all"
                        placeholder="Your Photography Co."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-charcoal mb-2">
                        Website *
                      </label>
                      <input
                        type="url"
                        required
                        value={businessForm.website}
                        onChange={(e) => setBusinessForm({...businessForm, website: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-charcoal/10 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all"
                        placeholder="https://yourwebsite.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-charcoal mb-2">
                        Supplier Type *
                      </label>
                      <select
                        required
                        value={businessForm.supplier_type}
                        onChange={(e) => setBusinessForm({...businessForm, supplier_type: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-charcoal/10 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-white"
                      >
                        <option value="">Select type...</option>
                        <option value="Photography">Photography</option>
                        <option value="Videography">Videography</option>
                        <option value="Photography & Videography">Photography & Videography</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-charcoal mb-2">
                        Instagram Handle
                      </label>
                      <input
                        type="text"
                        value={businessForm.instagram_handle}
                        onChange={(e) => setBusinessForm({...businessForm, instagram_handle: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-charcoal/10 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all"
                        placeholder="@yourhandle"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={businessSubmitting}
                      data-testid="submit-business-form-btn"
                      className="w-full py-4 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-bold uppercase tracking-wide hover:shadow-xl hover:shadow-gold/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                    >
                      {businessSubmitting ? 'Submitting...' : 'Submit Business'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function QuizPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const currentQuestion = QUIZ_QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUIZ_QUESTIONS.length) * 100;

  const handleAnswer = (value) => {
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: {
        question_id: currentQuestion.id,
        question_text: currentQuestion.question,
        answer: value
      }
    };
    setAnswers(newAnswers);

    if (currentStep < QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 300);
    } else {
      handleSubmit(newAnswers);
    }
  };

  const handleSubmit = async (finalAnswers) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/quiz/submit`,
        { answers: Object.values(finalAnswers) },
        { withCredentials: true }
      );
      navigate(`/results/${response.data.result_id}`);
    } catch (error) {
      console.error('Quiz submission error:', error);
      alert('Failed to submit quiz. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="texture-overlay" />
        <div className="relative z-10 text-center max-w-lg px-6">
          {/* Animated rings */}
          <div className="relative w-32 h-32 mx-auto mb-8">
            <div className="absolute inset-0 rounded-full border-4 border-gold/20 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="absolute inset-2 rounded-full border-4 border-gold/30 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.2s' }} />
            <div className="absolute inset-4 rounded-full border-4 border-gold/40 animate-ping" style={{ animationDuration: '3s', animationDelay: '0.4s' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-dim flex items-center justify-center animate-pulse">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
          
          {/* Loading text with typewriter effect */}
          <div className="space-y-3">
            <p className="text-2xl font-heading font-bold text-charcoal">Finding Your Perfect Match</p>
            <div className="flex items-center justify-center gap-2 text-charcoal/60">
              <span className="inline-block w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
              <span className="inline-block w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              <span className="inline-block w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
          
          {/* Fun progress steps */}
          <div className="mt-10 space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center gap-3 bg-gold/5 rounded-full px-5 py-3"
            >
              <Check className="w-5 h-5 text-gold" />
              <span className="text-sm text-charcoal/70">Analyzing your style preferences...</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.0 }}
              className="flex items-center gap-3 bg-gold/5 rounded-full px-5 py-3"
            >
              <Check className="w-5 h-5 text-gold" />
              <span className="text-sm text-charcoal/70">Matching with top-rated suppliers...</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.7 }}
              className="flex items-center gap-3 bg-gold/5 rounded-full px-5 py-3"
            >
              <Check className="w-5 h-5 text-gold" />
              <span className="text-sm text-charcoal/70">Compiling your perfect matches...</span>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative">
      <div className="texture-overlay" />
      
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-2 bg-charcoal/5 z-50">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="h-full bg-gradient-to-r from-gold to-gold-dim"
        />
      </div>

      <div className="relative z-10 px-6 md:px-12 py-16 min-h-screen flex items-center justify-center">
        <div className="max-w-4xl w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mb-8 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm uppercase tracking-wider text-charcoal/40 font-bold">
                    Question {currentStep + 1} of {QUIZ_QUESTIONS.length}
                  </span>
                  {currentStep === 0 && (
                    <span className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                      Free · No Sign Up
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {QUIZ_QUESTIONS.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-2 w-8 rounded-full transition-all duration-300 ${
                        idx <= currentStep ? 'bg-gold' : 'bg-charcoal/10'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-tight mb-16 text-charcoal">
                {currentQuestion.question}
              </h2>

              <div className="space-y-4">
                {currentQuestion.options.map((option, index) => {
                  const Icon = option.icon;
                  const isSelected = answers[currentQuestion.id]?.answer === option.value;
                  
                  return (
                    <motion.button
                      key={option.value}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      onClick={() => handleAnswer(option.value)}
                      data-testid={`quiz-option-${option.value}`}
                      className={`relative cursor-pointer border-2 bg-white p-8 transition-all duration-300 w-full text-left rounded-2xl group shadow-sm hover:shadow-xl ${
                        isSelected
                          ? 'border-gold bg-gold/5 shadow-lg'
                          : 'border-charcoal/10 hover:border-gold/50'
                      }`}
                    >
                      <div className="flex items-center gap-6">
                        {Icon && (
                          <div className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 ${
                            isSelected 
                              ? 'bg-gradient-to-br from-gold to-gold-dim' 
                              : 'bg-charcoal/5 group-hover:bg-gold/10'
                          }`}>
                            <Icon className={`w-7 h-7 ${isSelected ? 'text-white' : 'text-charcoal/70'}`} />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-xl font-semibold text-charcoal">{option.label}</p>
                        </div>
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                          isSelected 
                            ? 'border-gold bg-gold' 
                            : 'border-charcoal/20'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  data-testid="quiz-back-btn"
                  className="mt-12 flex items-center gap-2 text-sm uppercase tracking-wide text-charcoal/60 hover:text-charcoal font-semibold transition-colors duration-300"
                >
                  ← Back
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function ResultsPage() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    partnerName: '',
    email: '',
    mobile: '',
    venue: '',
    weddingDate: ''
  });
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const { resultId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await axios.get(`${API}/quiz/results/${resultId}`, { withCredentials: true });
        setResult(response.data);
      } catch (error) {
        console.error('Failed to fetch result:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [resultId]);

  const handleGetQuote = (supplier) => {
    setSelectedSupplier(supplier);
    setShowQuoteModal(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmittingQuote(true);
    
    try {
      await axios.post(`${API}/quote/request`, {
        ...quoteForm,
        selectedSupplier: selectedSupplier,
        resultId: resultId
      }, { withCredentials: true });
      
      setShowQuoteModal(false);
      setQuoteSuccess(true);
      setTimeout(() => setQuoteSuccess(false), 5000);
      setQuoteForm({
        name: '',
        partnerName: '',
        email: '',
        mobile: '',
        venue: '',
        weddingDate: ''
      });
    } catch (error) {
      console.error('Failed to send quote request:', error);
      alert('Failed to send quote request. Please try again.');
    } finally {
      setSubmittingQuote(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="texture-overlay" />
        <div className="relative z-10 text-center">
          <div className="w-20 h-20 rounded-full border-4 border-gold/20 border-t-gold animate-spin mx-auto mb-6" />
          <p className="text-xl font-semibold text-charcoal">Loading your matches...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="texture-overlay" />
        <div className="relative z-10 text-center">
          <p className="text-xl text-charcoal/70 mb-6">Result not found</p>
          <button
            onClick={() => navigate('/')}
            className="text-sm uppercase tracking-wide text-gold hover:text-gold-dim font-semibold transition-colors duration-300"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative">
      <div className="texture-overlay" />
      
      <header className="relative z-50 px-6 md:px-12 py-6 bg-white border-b border-charcoal/5 sticky top-0">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="font-heading text-2xl font-bold tracking-tight text-charcoal leading-none">
                Essex Photo & Video
              </div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold font-semibold mt-1">
                Finder
              </div>
            </div>
          </Link>
          <button
            onClick={() => navigate('/quiz')}
            data-testid="retake-quiz-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-charcoal text-white text-sm font-medium hover:bg-charcoal/90 transition-all duration-300"
          >
            Retake Quiz
          </button>
        </div>
      </header>

      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 px-6 md:px-12 py-20"
      >
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-20 text-center"
          >
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-xs uppercase tracking-wider text-gold font-bold">Your Perfect Matches</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-heading font-bold tracking-tight leading-tight mb-6 text-charcoal">
              We Found Your
              <span className="block gradient-text mt-2">Dream Suppliers</span>
            </h1>
            <p className="text-xl text-charcoal/60 max-w-2xl mx-auto">
              Based on your style and preferences, here are your top matches
            </p>
          </motion.div>

          {/* Results Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {result.matched_suppliers.map((supplier, index) => {
              // Map supplier images
              const supplierImages = {
                'wedding_video_essex': 'https://customer-assets.emergentagent.com/job_cine-search/artifacts/ti0hsg4k_WVE%20MAIN%20PHOTO.jpg',
                'chic_photography': 'https://customer-assets.emergentagent.com/job_cine-search/artifacts/9u2bjc1t_chic.avif',
                'click_play_weddings': 'https://static.wixstatic.com/media/1fe187_d6b7d99338ef48a28e6c5cef9aa966d7f000.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_avif,quality_auto/1fe187_d6b7d99338ef48a28e6c5cef9aa966d7f000.jpg',
                'maverick_weddings': 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1200&q=80',
                'pure_image': 'https://customer-assets.emergentagent.com/job_cine-search/artifacts/76k4zsgd_pureimagephoto.webp'
              };
              
              return (
              <motion.div
                key={supplier.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className={`group relative overflow-hidden bg-white border-2 border-charcoal/10 transition-all duration-500 hover:border-gold hover:shadow-2xl rounded-3xl ${
                  index === 0 ? 'lg:col-span-2' : ''
                }`}
                data-testid={`supplier-card-${supplier.id}`}
              >
                <div className={`relative overflow-hidden ${index === 0 ? 'h-96' : 'h-80'}`}>
                  <img
                    src={supplierImages[supplier.id] || 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80'}
                    alt={supplier.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    style={supplier.id === 'wedding_video_essex' ? { objectPosition: 'center 20%' } : {}}
                  />
                  <div className="image-overlay" />
                  <div className="absolute top-6 right-6 px-5 py-3 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-bold text-lg shadow-xl">
                    {supplier.match_score}% Match
                  </div>
                  {supplier.id === 'wedding_video_essex' ? (
                    <div className="absolute top-6 left-6 flex flex-col gap-2">
                      <div className="px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm flex items-center gap-1.5">
                        <Award className="w-4 h-4 text-gold" />
                        <span className="text-xs font-bold text-charcoal">7X Award Winners</span>
                      </div>
                      <div className="px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm flex items-center gap-1.5">
                        <Heart className="w-4 h-4 text-gold fill-gold" />
                        <span className="text-xs font-bold text-charcoal">700+ Weddings</span>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute top-6 left-6 px-4 py-2 rounded-full bg-white/95 backdrop-blur-sm flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-gold fill-gold" />
                      <span className="text-sm font-bold text-charcoal">5.0</span>
                    </div>
                  )}
                </div>
                
                <div className="p-8 lg:p-10">
                  <h3 className="text-3xl lg:text-4xl font-heading font-bold mb-3 text-charcoal group-hover:text-gold transition-colors duration-300">
                    {supplier.name}
                  </h3>
                  <p className="text-base text-charcoal/60 mb-4 leading-relaxed">
                    {supplier.description}
                  </p>
                  <div className="mb-6 p-4 bg-gold/5 border-l-4 border-gold rounded-r-lg">
                    <p className="text-sm font-medium italic text-charcoal/70">
                      "{supplier.match_reason}"
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleGetQuote(supplier)}
                      data-testid={`get-quote-${supplier.id}`}
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-bold uppercase tracking-wide hover:shadow-xl hover:shadow-gold/30 transition-all duration-300 hover:scale-105"
                    >
                      <Mail className="w-5 h-5" />
                      Get Quote
                    </button>
                    <a
                      href={supplier.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`supplier-link-${supplier.id}`}
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full border-2 border-charcoal/30 text-charcoal font-bold uppercase tracking-wide hover:border-charcoal hover:bg-charcoal hover:text-white transition-all duration-300"
                    >
                      View Portfolio
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <button
              onClick={() => navigate('/quiz')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-charcoal/20 text-charcoal font-semibold hover:border-gold hover:text-gold transition-all duration-300"
            >
              Not quite right? Retake Quiz
            </button>
          </div>
        </div>
      </motion.section>

      {/* Quote Request Modal */}
      <AnimatePresence>
        {showQuoteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowQuoteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl p-8 md:p-12 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowQuoteModal(false)}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-charcoal/5 hover:bg-charcoal/10 flex items-center justify-center transition-colors duration-300"
              >
                <X className="w-5 h-5 text-charcoal" />
              </button>

              <div className="mb-8">
                <h2 className="text-4xl font-heading font-bold text-charcoal mb-3">
                  Request a Quote
                </h2>
                <p className="text-charcoal/60">
                  Your details will be sent to {selectedSupplier?.name}
                </p>
              </div>

              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-charcoal mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-charcoal mb-2">
                      Partner's Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={quoteForm.partnerName}
                      onChange={(e) => setQuoteForm({...quoteForm, partnerName: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300"
                      placeholder="Jane"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-charcoal mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-charcoal mb-2">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={quoteForm.mobile}
                      onChange={(e) => setQuoteForm({...quoteForm, mobile: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300"
                      placeholder="+44 7XXX XXXXXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-charcoal mb-2">
                    Wedding Venue *
                  </label>
                  <input
                    type="text"
                    required
                    value={quoteForm.venue}
                    onChange={(e) => setQuoteForm({...quoteForm, venue: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300"
                    placeholder="The Grand Hotel, Essex"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-charcoal mb-2">
                    Wedding Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={quoteForm.weddingDate}
                    onChange={(e) => setQuoteForm({...quoteForm, weddingDate: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-charcoal/10 focus:border-gold focus:outline-none transition-colors duration-300"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submittingQuote}
                    className="w-full py-4 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-bold uppercase tracking-wide hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingQuote ? 'Sending...' : 'Send Quote Request'}
                  </button>
                  <p className="text-xs text-center text-charcoal/50 mt-4">
                    Your details will be sent to {selectedSupplier?.name}
                  </p>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Toast */}
      <AnimatePresence>
        {quoteSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
          >
            <div className="bg-gradient-to-r from-charcoal to-charcoal/95 text-white px-8 py-5 rounded-2xl shadow-2xl flex items-center gap-4 border border-gold/20">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-gold to-gold-dim flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-lg">Quote Request Sent!</p>
                <p className="text-white/70 text-sm">You'll be contacted shortly by our team</p>
              </div>
              <button
                onClick={() => setQuoteSuccess(false)}
                className="ml-4 p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DashboardPage() {
  const [user, setUser] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userResponse.data);
        
        const resultsResponse = await axios.get(`${API}/quiz/my-results`, { withCredentials: true });
        setResults(resultsResponse.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, location.state]);

  useEffect(() => {
    if (user && results.length === 0 && !loading) {
      const fetchResults = async () => {
        try {
          const resultsResponse = await axios.get(`${API}/quiz/my-results`, { withCredentials: true });
          setResults(resultsResponse.data);
        } catch (error) {
          console.error('Failed to fetch results:', error);
        }
      };
      fetchResults();
    }
  }, [user, results.length, loading]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="texture-overlay" />
        <div className="relative z-10 text-center">
          <div className="w-20 h-20 rounded-full border-4 border-gold/20 border-t-gold animate-spin mx-auto mb-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative">
      <div className="texture-overlay" />
      
      <header className="relative z-10 px-6 md:px-12 py-6 bg-white/95 backdrop-blur-md border-b border-charcoal/5">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="font-heading text-2xl font-bold tracking-tight text-charcoal leading-none">
                Essex Photo & Video
              </div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold font-semibold mt-1">
                Finder
              </div>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-charcoal/10 text-sm font-medium text-charcoal hover:bg-charcoal/5 transition-all duration-300"
          >
            Sign Out
          </button>
        </div>
      </header>

      <section className="relative z-10 px-6 md:px-12 py-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16">
            <h1 className="text-5xl md:text-6xl font-heading font-bold tracking-tight mb-4 text-charcoal">
              Welcome back, {user?.name}
            </h1>
            <p className="text-lg text-charcoal/60">
              {user?.email}
            </p>
          </div>

          <div className="mb-12">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-heading font-semibold text-charcoal">Your Quiz Results</h2>
              <button
                onClick={() => navigate('/quiz')}
                data-testid="dashboard-new-quiz-btn"
                className="flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white font-bold uppercase tracking-wide hover:shadow-xl transition-all duration-300"
              >
                <Sparkles className="w-5 h-5" />
                Take New Quiz
              </button>
            </div>

            {results.length === 0 ? (
              <div className="bg-gradient-to-br from-pearl to-white border-2 border-charcoal/10 rounded-3xl p-16 text-center">
                <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-10 h-10 text-gold" />
                </div>
                <p className="text-xl font-semibold text-charcoal mb-6">You haven't taken any quizzes yet</p>
                <button
                  onClick={() => navigate('/quiz')}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-charcoal text-white font-bold uppercase tracking-wide hover:bg-charcoal/90 transition-all duration-300"
                >
                  Start Your First Quiz
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {results.map((result) => (
                  <div
                    key={result.result_id}
                    data-testid={`result-item-${result.result_id}`}
                    className="bg-white border-2 border-charcoal/10 rounded-2xl p-8 hover:border-gold hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/results/${result.result_id}`)}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-charcoal/40 font-bold mb-2">
                          {new Date(result.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                        <p className="text-lg font-semibold text-charcoal">
                          {result.matched_suppliers.length} matches found
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-gold font-bold">
                        View
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {result.matched_suppliers.slice(0, 3).map((supplier) => (
                        <span
                          key={supplier.id}
                          className="text-xs px-3 py-1.5 bg-gold/10 text-charcoal font-medium rounded-full"
                        >
                          {supplier.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        navigate('/dashboard', { state: { user: response.data }, replace: true });
      } catch (error) {
        console.error('Auth failed:', error);
        navigate('/');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="texture-overlay" />
      <div className="relative z-10 text-center">
        <div className="w-20 h-20 rounded-full border-4 border-gold/20 border-t-gold animate-spin mx-auto mb-6" />
        <p className="text-xl font-semibold text-charcoal">Signing you in...</p>
      </div>
    </div>
  );
}

function useParams() {
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  return { resultId: pathParts[pathParts.length - 1] };
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/quiz" element={<QuizPage />} />
      <Route path="/results/:resultId" element={<ResultsPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
    </Routes>
  );
}

function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto bg-charcoal/95 backdrop-blur-lg rounded-2xl border border-white/10 shadow-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-gold" />
                </div>
                <h3 className="text-white font-heading font-semibold text-lg">We Value Your Privacy</h3>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                We use cookies to enhance your browsing experience, provide personalised recommendations, and analyse our traffic. 
                By clicking "Accept All", you consent to our use of cookies.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={handleDecline}
                data-testid="cookie-decline-btn"
                className="px-6 py-3 rounded-full border border-white/20 text-white/80 text-sm font-medium hover:bg-white/10 transition-all duration-300"
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                data-testid="cookie-accept-btn"
                className="px-8 py-3 rounded-full bg-gradient-to-r from-gold to-gold-dim text-white text-sm font-bold hover:shadow-lg hover:shadow-gold/20 transition-all duration-300"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <CookieBanner />
    </BrowserRouter>
  );
}

export default App;
